// packages/web/src/services/teacherExerciseApi.ts
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000';

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
});

// 请求拦截器：添加 token
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 响应拦截器：统一错误处理
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export interface ExerciseTemplate {
  id: string;
  title: string;
  initial_question: string;
  category: string;
  teacher_requirement?: string;
  knowledge_points?: any[];
  created_at: string;
  is_assigned: boolean;
  assignment_count: number;
}

export interface AssignmentDetail {
  homework_id: string;
  homework_name: string;
  assigned_at: string;
  student_count: number;
}

export const teacherExerciseApi = {
  /**
   * 获取习题模板列表（带布置状态）
   */
  async getTemplates(filters?: {
    category?: string;
    assignmentStatus?: 'all' | 'assigned' | 'unassigned';
    startDate?: string;
    endDate?: string;
  }): Promise<{ success: boolean; data: { exercises: ExerciseTemplate[] } }> {
    const params = new URLSearchParams();
    if (filters?.category) params.append('category', filters.category);
    if (filters?.assignmentStatus) params.append('assignmentStatus', filters.assignmentStatus);
    if (filters?.startDate) params.append('startDate', filters.startDate);
    if (filters?.endDate) params.append('endDate', filters.endDate);

    const response = await apiClient.get(`/teach/exercise-management/templates?${params.toString()}`);
    return response.data;
  },

  /**
   * 获取习题的布置情况
   */
  async getAssignmentStatus(exerciseId: string): Promise<{
    success: boolean;
    data: {
      exercise_id: string;
      is_assigned: boolean;
      assignments: AssignmentDetail[];
    };
  }> {
    const response = await apiClient.get(`/teach/exercise-management/templates/${exerciseId}/assignments`);
    return response.data;
  },

  /**
   * 更新习题教学要求
   */
  async updateRequirement(exerciseId: string, requirement: string): Promise<{ success: boolean }> {
    const response = await apiClient.put(`/teach/exercise-management/templates/${exerciseId}/requirement`, {
      teacher_requirement: requirement,
    });
    return response.data;
  },

  /**
   * 删除单个习题
   */
  async deleteExercise(exerciseId: string): Promise<{ success: boolean }> {
    const response = await apiClient.delete(`/teach/exercise-management/templates/${exerciseId}`);
    return response.data;
  },

  /**
   * 批量删除习题
   */
  async batchDelete(exerciseIds: string[]): Promise<{
    success: boolean;
    data: {
      deletedCount: number;
      skippedCount: number;
      skippedIds: string[];
    };
  }> {
    const response = await apiClient.post('/teach/exercise-management/templates/batch-delete', {
      ids: exerciseIds,
    });
    return response.data;
  },
};
